import { useCallback, useEffect, useMemo, useState } from 'react';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import LoadingIcons from 'react-loading-icons';
import useRequest from '../../../../hooks/useRequest';
import { BotComplexParams, BotTimeFrames } from '../../../Detail/types';
import { SingleProfitCalculationApiResponse } from '../../types';
import BasicInfoForm from '../Form/Basic';
import SpcResultView from '../../../Subscription/Content/Calculations/SpcResultView';
import '../SingleProfitCalculation/index.css';
import { colors } from '../../../../colors';
import { useLocation } from 'react-router-dom';
import {
  ProfitByPeriodApiResponse,
  ProfitCalculationProfitData,
} from '../../../Detail/Charts/types';
import { Exchange } from '../../../../types/exchange';

import ServicePaths from '../../../../bridge/utils/ServicePaths';
import FormSubmit from '../../../../components/Form/Submit';

interface PassedProps {
  baseAsset: string;
  quoteAsset: string;
  startDate: number;
  timeFrames: BotTimeFrames;
  complexParams: BotComplexParams;
  exchange: Exchange;
}

interface Props {
  setProfitByPeriodData: React.Dispatch<
    React.SetStateAction<ProfitByPeriodApiResponse | undefined>
  >;
  setProfitData: React.Dispatch<
    React.SetStateAction<ProfitCalculationProfitData | undefined>
  >;
}

const formatDateToUsableInputValue = (date: Date) => date.toJSON().slice(0, 10);

const ComplexParamsCalculation = (props: Props) => {
  const { setProfitByPeriodData, setProfitData } = props;
  const { state: passedProps }: { state: PassedProps | undefined } =
    useLocation();

  const [baseAsset, setBaseAsset] = useState(passedProps?.baseAsset || 'BTC');
  const [quoteAsset, setQuoteAsset] = useState(
    passedProps?.quoteAsset || 'USDC',
  );
  const [exchange, setExchange] = useState(
    passedProps?.exchange || Object.values(Exchange)[3],
  );

  const [startDate, setStartDate] = useState(
    !!passedProps?.startDate
      ? formatDateToUsableInputValue(new Date(passedProps.startDate))
      : '2017-01-01',
  );
  const [endDate, setEndDate] = useState(
    formatDateToUsableInputValue(
      new Date(new Date().valueOf() + 1000 * 60 * 60 * 24),
    ),
  );

  const [shouldFetch, setShouldFetch] = useState(!!passedProps);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isCollapsable, setIsCollapsable] = useState(false);

  const params = useMemo(
    () => ({
      baseAsset,
      quoteAsset,
      exchange,
      startTimestamp: new Date(startDate).valueOf(),
      endTimestamp: new Date(endDate).valueOf(),
    }),
    [baseAsset, quoteAsset, exchange, startDate, endDate],
  );

  const options = useMemo<{ method: 'POST'; shouldFetch: boolean }>(
    () => ({
      method: 'POST',
      shouldFetch,
    }),
    [shouldFetch],
  );

  const {
    data,
    isValidating,
  }: {
    data: SingleProfitCalculationApiResponse | undefined;
    isValidating: boolean;
  } = useRequest(
    ServicePaths.PROFIT_CALCULATIONS.GetBestProfitCalculation,
    params,
    options,
  );

  const toggleCollapse = useCallback(() => {
    if (!isCollapsable) {
      return;
    }

    setIsCollapsed(!isCollapsed);
  }, [isCollapsable, isCollapsed]);

  useEffect(() => {
    if (data && !isCollapsable) {
      setIsCollapsable(true);
      setIsCollapsed(true);
    }
  }, [data, isCollapsable]);

  useEffect(() => {
    if (!data) {
      return;
    }

    setProfitData({
      stable: data.totalTradingStableProfit,
      asset: data.totalTradingAssetProfit,
      price: data.totalWithoutTradingStableProfit,
      priceSinceFirstTransaction:
        data.totalWithoutTradingStableProfitSinceFirstTransaction,
    });

    setProfitByPeriodData({
      stable: Object.entries(data.endBalanceInUsdtHistory).map(
        ([timestamp, value]) => ({ timestamp: parseInt(timestamp, 10), value }),
      ),
      asset: Object.entries(data.endBalanceInAssetHistory).map(
        ([timestamp, value]) => ({ timestamp: parseInt(timestamp, 10), value }),
      ),
    });
  }, [data, setProfitByPeriodData, setProfitData]);

  return (
    <>
      <div
        className="single-profit-calculation-header"
        onClick={toggleCollapse}
      >
        <h3 className="single-profit-calculation-label">Form:</h3>
        {isValidating ? (
          <div className="single-profit-calculation-loader">
            <LoadingIcons.Grid width={20} height={20} />
          </div>
        ) : (
          isCollapsable &&
          (isCollapsed ? (
            <FaChevronDown
              color={colors.white}
              size={24}
              className={'chevron chevron-down'}
            />
          ) : (
            <FaChevronUp
              color={colors.white}
              size={24}
              className={'chevron chevron-down'}
            />
          ))
        )}
      </div>

      {!isCollapsed && (
        <div className="single-profit-calculation-content">
          <BasicInfoForm
            baseAsset={baseAsset}
            setBaseAsset={setBaseAsset}
            quoteAsset={quoteAsset}
            setQuoteAsset={setQuoteAsset}
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
            setShouldFetch={setShouldFetch}
            exchange={exchange}
            setExchange={setExchange}
          />
          <FormSubmit
            isLoading={isValidating}
            onClick={() => {
              setShouldFetch(true);
            }}
          />
        </div>
      )}

      <div className="single-profit-calcultion-divider" />

      {!!data && isCollapsed && <SpcResultView data={data} />}
    </>
  );
};

export default ComplexParamsCalculation;
